/**
 * Orchestra AI Platform - Scenarios Service
 * 
 * Service for fetching and managing educational scenarios from Supabase
 * 
 * @fileoverview Scenarios service with Supabase integration
 */

import { supabase } from '../lib/supabase';

/**
 * Normalize scenario data from database
 * Maps database column names to expected field names
 * @param {Object} scenario - Raw scenario from database
 * @returns {Object} Normalized scenario
 */
function normalizeScenario(scenario) {
  if (!scenario) return null;
  
  // Map w_template to workflow_template if needed
  const normalized = {
    ...scenario,
    workflow_template: scenario.workflow_template || scenario.w_template || {
      nodes: [],
      edges: [],
      viewport: { x: 0, y: 0, zoom: 1 }
    }
  };
  
  // Ensure workflow_template has proper structure
  if (normalized.workflow_template) {
    if (!normalized.workflow_template.nodes) {
      normalized.workflow_template.nodes = [];
    }
    if (!normalized.workflow_template.edges) {
      normalized.workflow_template.edges = [];
    }
    if (!normalized.workflow_template.viewport) {
      normalized.workflow_template.viewport = { x: 0, y: 0, zoom: 1 };
    }
  }
  
  return normalized;
}

/**
 * Fetch all scenarios from Supabase
 * @returns {Promise<Object[]>} Array of scenarios
 */
export async function fetchAllScenarios() {
  try {
    const { data, error } = await supabase
      .from('developer_scenarios')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    // Normalize all scenarios
    return (data || []).map(normalizeScenario);
  } catch (error) {
    console.error('[Scenarios Service] Failed to fetch scenarios:', error);
    throw error;
  }
}

/**
 * Fetch scenario by ID
 * @param {string} scenarioId - Scenario ID
 * @returns {Promise<Object|null>} Scenario object or null
 */
export async function fetchScenarioById(scenarioId) {
  try {
    const { data, error } = await supabase
      .from('developer_scenarios')
      .select('*')
      .eq('id', scenarioId)
      .single();

    if (error) throw error;
    return normalizeScenario(data);
  } catch (error) {
    console.error('[Scenarios Service] Failed to fetch scenario:', error);
    return null;
  }
}

/**
 * Fetch popular scenarios
 * @returns {Promise<Object[]>} Array of popular scenarios
 */
export async function fetchPopularScenarios() {
  try {
    const { data, error } = await supabase
      .from('developer_scenarios')
      .select('*')
      .eq('is_popular', true)
      .order('usage_count', { ascending: false })
      .limit(10);

    if (error) throw error;
    return (data || []).map(normalizeScenario);
  } catch (error) {
    console.error('[Scenarios Service] Failed to fetch popular scenarios:', error);
    return [];
  }
}

/**
 * Fetch scenarios by category
 * @param {string} category - Category name
 * @returns {Promise<Object[]>} Array of scenarios
 */
export async function fetchScenariosByCategory(category) {
  try {
    const { data, error } = await supabase
      .from('developer_scenarios')
      .select('*')
      .eq('category', category)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(normalizeScenario);
  } catch (error) {
    console.error('[Scenarios Service] Failed to fetch scenarios by category:', error);
    return [];
  }
}

/**
 * Fetch scenarios by difficulty
 * @param {string} difficulty - Difficulty level
 * @returns {Promise<Object[]>} Array of scenarios
 */
export async function fetchScenariosByDifficulty(difficulty) {
  try {
    const { data, error } = await supabase
      .from('developer_scenarios')
      .select('*')
      .eq('difficulty', difficulty)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(normalizeScenario);
  } catch (error) {
    console.error('[Scenarios Service] Failed to fetch scenarios by difficulty:', error);
    return [];
  }
}

/**
 * Search scenarios
 * @param {string} query - Search query
 * @returns {Promise<Object[]>} Array of matching scenarios
 */
export async function searchScenarios(query) {
  try {
    const { data, error } = await supabase
      .from('developer_scenarios')
      .select('*')
      .or(`name.ilike.%${query}%,description.ilike.%${query}%`)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(normalizeScenario);
  } catch (error) {
    console.error('[Scenarios Service] Failed to search scenarios:', error);
    return [];
  }
}

/**
 * Increment scenario usage count
 * @param {string} scenarioId - Scenario ID
 */
export async function incrementScenarioUsage(scenarioId) {
  try {
    const { error } = await supabase.rpc('increment_scenario_usage', {
      scenario_id: scenarioId,
    });

    if (error) {
      // Fallback: manual update if RPC doesn't exist
      const { data: scenario } = await fetchScenarioById(scenarioId);
      if (scenario) {
        await supabase
          .from('developer_scenarios')
          .update({ usage_count: (scenario.usage_count || 0) + 1 })
          .eq('id', scenarioId);
      }
    }
  } catch (error) {
    console.error('[Scenarios Service] Failed to increment usage:', error);
  }
}

